import type {
  ActivityDefinition,
  CodeableConcept,
  Extension,
  PlanDefinition,
  PlanDefinitionAction,
  PlanDefinitionGoal,
  Quantity,
  Timing,
} from '@medplum/fhirtypes';

import {
  ACTIVITY_CANONICAL_BASE,
  ELUVE_PLAN_SECTION_SYSTEM,
  EXERCISE_DOSAGE_EXTENSION_URL,
  PLAN_CANONICAL_BASE,
  PLAN_SHAPE_EXTENSION_URL,
  UCUM_SYSTEM,
  pascalName,
  slugify,
} from './constants';
import type {
  ActivityDosage,
  Cadence,
  CodedConcept,
  GoalTarget,
  PlanActivity,
  PlanGoal,
  PlanShapeCode,
  PlanSpec,
} from './spec';

// A compiled plan: the PlanDefinition plus the plan-scoped ActivityDefinitions
// its activity child actions reference. Saved together in one transaction
// Bundle; validated together in present_plan.
export type CompiledPlan = {
  planDefinition: PlanDefinition;
  activityDefinitions: ActivityDefinition[];
};

// Decode-side context: the plan's resolved ActivityDefinitions (fetched by
// canonical). Optional — without them, activity entries degrade to what the
// child actions carry (name, dosage, frequency).
export type DecodeContext = {
  activityDefinitions?: ActivityDefinition[];
};

// --- CodedConcept ↔ CodeableConcept -----------------------------------------

export function toCodeableConcept(concept: CodedConcept): CodeableConcept {
  return {
    text: concept.text,
    coding:
      concept.system && concept.code
        ? [
            {
              system: concept.system,
              code: concept.code,
              display: concept.text,
            },
          ]
        : undefined,
  };
}

// Tolerant decode: stored plans may carry only codings with displays, only
// text, or both.
export function fromCodeableConcept(
  cc: CodeableConcept | undefined,
): CodedConcept | undefined {
  if (!cc) {
    return undefined;
  }
  const coding = cc.coding?.[0];
  const text = cc.text ?? coding?.display;
  if (!text) {
    return undefined;
  }
  return {
    text,
    system: coding?.system,
    code: coding?.code,
  };
}

// A coding under the plan-section system marking which structural slot of a
// shape this action/goal fills (baseline/event/cadence/review-rule/control-band).
export function sectionConcept(code: string, display: string): CodeableConcept {
  return { coding: [{ system: ELUVE_PLAN_SECTION_SYSTEM, code, display }] };
}

export function sectionCodeOf(
  concepts: CodeableConcept[] | undefined,
): string | undefined {
  for (const cc of concepts ?? []) {
    const code = cc.coding?.find(
      (c) => c.system === ELUVE_PLAN_SECTION_SYSTEM,
    )?.code;
    if (code) {
      return code;
    }
  }
  return undefined;
}

// --- Goals ↔ PlanDefinition.goal ---------------------------------------------

export function toGoalTarget(
  target: GoalTarget,
): NonNullable<PlanDefinitionGoal['target']>[number] {
  const quantity = (value: number): Quantity => ({ value, unit: target.unit });
  return {
    measure: toCodeableConcept(target.measure),
    // Range when bounded; single value compiles to detailQuantity.
    ...(target.low !== undefined || target.high !== undefined
      ? {
          detailRange: {
            low: target.low !== undefined ? quantity(target.low) : undefined,
            high: target.high !== undefined ? quantity(target.high) : undefined,
          },
        }
      : target.value !== undefined
        ? { detailQuantity: quantity(target.value) }
        : {}),
  };
}

export function fromGoalTarget(
  target: NonNullable<PlanDefinitionGoal['target']>[number],
): GoalTarget | undefined {
  const measure = fromCodeableConcept(target.measure);
  if (!measure) {
    return undefined;
  }
  return {
    measure,
    value: target.detailQuantity?.value,
    low: target.detailRange?.low?.value,
    high: target.detailRange?.high?.value,
    unit:
      target.detailQuantity?.unit ??
      target.detailRange?.low?.unit ??
      target.detailRange?.high?.unit,
  };
}

export function toFhirGoal(
  goal: PlanGoal,
  category?: CodeableConcept,
): PlanDefinitionGoal {
  const targets = (goal.targets ?? []).map(toGoalTarget);
  return {
    category,
    description: { text: goal.description },
    target: targets.length > 0 ? targets : undefined,
  };
}

export function fromFhirGoal(goal: PlanDefinitionGoal): PlanGoal | undefined {
  const description =
    goal.description?.text ?? goal.description?.coding?.[0]?.display;
  if (!description) {
    return undefined;
  }
  const targets = (goal.target ?? [])
    .map(fromGoalTarget)
    .filter((t): t is GoalTarget => t !== undefined);
  return { description, targets: targets.length > 0 ? targets : undefined };
}

// --- Cadence ↔ Timing ---------------------------------------------------------

// Duration/cadence ride PlanDefinition.action.timingTiming: boundsDuration is
// the phase length, frequency/period/periodUnit the visit cadence.
export function toTiming(args: {
  durationDays?: number;
  cadence?: Cadence;
}): Timing | undefined {
  if (args.durationDays === undefined && !args.cadence) {
    return undefined;
  }
  return {
    repeat: {
      boundsDuration:
        args.durationDays !== undefined
          ? {
              value: args.durationDays,
              unit: 'days',
              system: UCUM_SYSTEM,
              code: 'd',
            }
          : undefined,
      frequency: args.cadence?.frequency,
      period: args.cadence ? (args.cadence.period ?? 1) : undefined,
      periodUnit: args.cadence?.periodUnit,
    },
  };
}

export function fromTiming(timing: Timing | undefined): {
  durationDays?: number;
  cadence?: Cadence;
} {
  const repeat = timing?.repeat;
  if (!repeat) {
    return {};
  }
  const periodUnit = repeat.periodUnit;
  return {
    durationDays:
      repeat.boundsDuration?.code === 'd'
        ? repeat.boundsDuration.value
        : undefined,
    cadence:
      repeat.frequency &&
      (periodUnit === 'd' || periodUnit === 'wk' || periodUnit === 'mo')
        ? {
            frequency: repeat.frequency,
            period:
              repeat.period !== undefined && repeat.period !== 1
                ? repeat.period
                : undefined,
            periodUnit,
          }
        : undefined,
  };
}

// --- Shared resource skeleton --------------------------------------------------

// Everything outside the per-shape spine. Deterministic: the canonical url
// (slug from title), version 1.0.0, and `status: active` are part of the
// compile, so the $validate gate in present_plan exercises exactly the
// resource Save inserts (save-is-publish).
export function buildPlanDefinitionBase(
  spec: PlanSpec,
  shape: PlanShapeCode,
  goals: PlanDefinitionGoal[],
): PlanDefinition {
  return {
    resourceType: 'PlanDefinition',
    url: `${PLAN_CANONICAL_BASE}/${slugify(spec.title)}`,
    version: '1.0.0',
    name: pascalName(spec.title),
    title: spec.title,
    status: 'active',
    description: spec.description,
    extension: [
      { url: PLAN_SHAPE_EXTENSION_URL, valueCode: shape } satisfies Extension,
    ],
    topic: spec.condition ? [toCodeableConcept(spec.condition)] : undefined,
    goal: goals.length > 0 ? goals : undefined,
  };
}

// --- Plan Activities ↔ ActivityDefinition + child actions ----------------------

// FHIRPath string literals are single-quoted with backslash escapes. Used to
// carry patientInstruction prose through ActivityDefinition.dynamicValue
// (Expression's exp-1 invariant demands a computable `expression`).
export function toFhirPathStringLiteral(text: string): string {
  return `'${text.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

export function fromFhirPathStringLiteral(
  expression: string | undefined,
): string | undefined {
  if (!expression) {
    return undefined;
  }
  const match = /^'([\s\S]*)'$/.exec(expression);
  if (!match) {
    return undefined;
  }
  return (match[1] ?? '').replace(/\\(['\\])/g, '$1');
}

// Machine-readable dosing as one complex extension: the spec-gap mitigation
// for sets×reps×hold, which has no first-class FHIR home.
export function toDosageExtension(
  dosage: ActivityDosage,
): Extension | undefined {
  const sub: Extension[] = [];
  if (dosage.sets !== undefined) {
    sub.push({ url: 'sets', valueInteger: dosage.sets });
  }
  if (dosage.reps !== undefined) {
    sub.push({ url: 'reps', valueInteger: dosage.reps });
  }
  if (dosage.holdSeconds !== undefined) {
    sub.push({ url: 'holdSeconds', valueInteger: dosage.holdSeconds });
  }
  if (dosage.resistance !== undefined) {
    sub.push({ url: 'resistance', valueString: dosage.resistance });
  }
  if (sub.length === 0) {
    return undefined;
  }
  return { url: EXERCISE_DOSAGE_EXTENSION_URL, extension: sub };
}

export function fromDosageExtension(
  extensions: Extension[] | undefined,
): ActivityDosage | undefined {
  const ext = extensions?.find((e) => e.url === EXERCISE_DOSAGE_EXTENSION_URL);
  if (!ext) {
    return undefined;
  }
  const sub = (url: string) => ext.extension?.find((e) => e.url === url);
  const dosage: ActivityDosage = {
    sets: sub('sets')?.valueInteger,
    reps: sub('reps')?.valueInteger,
    holdSeconds: sub('holdSeconds')?.valueInteger,
    resistance: sub('resistance')?.valueString,
  };
  return Object.values(dosage).some((v) => v !== undefined)
    ? dosage
    : undefined;
}

// The exercise-intrinsic half of the split: what the activity IS — code,
// description, patient instructions, default dosing/timing. $apply-ready:
// kind ServiceRequest + participant patient is the definitional form of a
// home-exercise prescription.
export function buildActivityDefinition(
  activity: PlanActivity,
  url: string,
): ActivityDefinition {
  const dosageExt = activity.dosage
    ? toDosageExtension(activity.dosage)
    : undefined;
  return {
    resourceType: 'ActivityDefinition',
    url,
    version: '1.0.0',
    name: pascalName(activity.name),
    title: activity.name,
    status: 'active',
    description: activity.description,
    kind: 'ServiceRequest',
    intent: 'order',
    // The patient-independent form of "performed as self-care".
    participant: [{ type: 'patient' }],
    code: activity.code ? toCodeableConcept(activity.code) : undefined,
    timingTiming: toTiming({ cadence: activity.frequency }),
    extension: dosageExt ? [dosageExt] : undefined,
    dynamicValue: activity.patientInstructions
      ? [
          {
            path: 'patientInstruction',
            expression: {
              language: 'text/fhirpath',
              expression: toFhirPathStringLiteral(activity.patientInstructions),
            },
          },
        ]
      : undefined,
  };
}

// The protocol-specific half: the child action referencing the definition,
// carrying THIS occurrence's dosing/frequency (action overrides definition on
// overlap — FHIR's $apply semantics, so per-phase dosing of one exercise works).
export function activityToChildAction(
  activity: PlanActivity,
  definitionUrl: string,
  id: string,
): PlanDefinitionAction {
  const dosageExt = activity.dosage
    ? toDosageExtension(activity.dosage)
    : undefined;
  return {
    id,
    title: activity.name,
    code: [sectionConcept('activity', 'Activity')],
    definitionCanonical: definitionUrl,
    timingTiming: toTiming({ cadence: activity.frequency }),
    extension: dosageExt ? [dosageExt] : undefined,
  };
}

// One compiler per plan compile: mints ADs deduped by activity slug (same name
// = same exercise, first occurrence's intrinsic fields win) and emits one
// child action per occurrence.
export type ActivityCompiler = {
  compile: (
    activities: PlanActivity[] | undefined,
    idPrefix: string,
  ) => PlanDefinitionAction[] | undefined;
  definitions: () => ActivityDefinition[];
};

export function createActivityCompiler(planTitle: string): ActivityCompiler {
  const planSlug = slugify(planTitle);
  const bySlug = new Map<string, ActivityDefinition>();
  return {
    compile(activities, idPrefix) {
      if (!activities || activities.length === 0) {
        return undefined;
      }
      return activities.map((activity, i) => {
        const slug = slugify(activity.name);
        const url = `${ACTIVITY_CANONICAL_BASE}/${planSlug}/${slug}`;
        if (!bySlug.has(slug)) {
          bySlug.set(slug, buildActivityDefinition(activity, url));
        }
        return activityToChildAction(
          activity,
          url,
          `${idPrefix}-activity-${i + 1}`,
        );
      });
    },
    definitions: () => [...bySlug.values()],
  };
}

// An action that represents a Plan Activity: tagged with the activity section
// code (authored plans) or carrying a definition reference (foreign plans).
export function isActivityAction(action: PlanDefinitionAction): boolean {
  return (
    sectionCodeOf(action.code) === 'activity' ||
    action.definitionCanonical !== undefined
  );
}

// Tolerant decode of activity child actions. Occurrence fields (name, dosage,
// frequency) come off the action; intrinsic fields enrich from the resolved
// ActivityDefinition when the caller supplied it — absent ADs degrade cleanly.
export function decodeActivityActions(
  actions: PlanDefinitionAction[] | undefined,
  ctx?: DecodeContext,
): PlanActivity[] | undefined {
  const adByUrl = new Map<string, ActivityDefinition>();
  for (const ad of ctx?.activityDefinitions ?? []) {
    if (ad.url) {
      adByUrl.set(ad.url, ad);
    }
  }
  const out: PlanActivity[] = [];
  for (const action of actions ?? []) {
    if (!isActivityAction(action)) {
      continue;
    }
    const canonical = action.definitionCanonical?.split('|')[0];
    const ad = canonical ? adByUrl.get(canonical) : undefined;
    const name = action.title ?? ad?.title ?? ad?.name;
    if (!name) {
      continue;
    }
    out.push({
      name,
      code: fromCodeableConcept(ad?.code),
      description: ad?.description,
      dosage:
        fromDosageExtension(action.extension) ??
        fromDosageExtension(ad?.extension),
      frequency:
        fromTiming(action.timingTiming).cadence ??
        fromTiming(ad?.timingTiming).cadence,
      patientInstructions: fromFhirPathStringLiteral(
        ad?.dynamicValue?.find((d) => d.path === 'patientInstruction')
          ?.expression?.expression,
      ),
    });
  }
  return out.length > 0 ? out : undefined;
}

// The shared layer back out of a stored plan. Goals can be filtered (the
// continuous decoder pulls control-band goals out separately).
export function decodeSharedFields(
  pd: PlanDefinition,
  goalFilter: (goal: PlanDefinitionGoal) => boolean = () => true,
): {
  title: string;
  description?: string;
  condition?: CodedConcept;
  goals?: PlanGoal[];
} {
  const goals = (pd.goal ?? [])
    .filter(goalFilter)
    .map(fromFhirGoal)
    .filter((g): g is PlanGoal => g !== undefined);
  return {
    title: pd.title ?? pd.name ?? 'Untitled plan',
    description: pd.description,
    condition: fromCodeableConcept(pd.topic?.[0]),
    goals: goals.length > 0 ? goals : undefined,
  };
}
